import { addDays, startOfWeek, startOfMonth } from "date-fns";
import { isEqual, isNull } from "lodash";

import CodeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";

const { rpPolicyCd, buckets, rpDemandDistributionPolicy } = CodeUtil;
const { P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM } = rpPolicyCd;
const { DAY, WEEK, MONTH } = buckets;
const { EQUAL_BY_BUCKET } = rpDemandDistributionPolicy;

const now = new ZDate();
const configDefaultValue = {
  bucket: DAY,
  range: 90,
  start: now,
  end: addDays(now, 90),
  demandDistributionPolicy: EQUAL_BY_BUCKET,
  salesRefStart: -6,
  salesRefEnd: 0,
  demandRefStartDate: 0,
  demandRefEndDate: 0,
  backorder: "notAllow",
  immediateShipment : "shipmentSame",
  kitDemandMergeFlg: false, 
  useTargetSupplyDays: false,
  noteFlg: false,
  multiSourcingFlg: false
};

const getToolTip = version => {
  if (version?.planConfigObj) {
    const { BUCKET_UNIT, PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;

    if (isEqual(BUCKET_UNIT, MONTH)) {
      const startMonth = dateUtils.formatDate(PLAN_START_DATE, "yyyy-MM");
      const endMonth = dateUtils.formatDate(PLAN_END_DATE, "yyyy-MM");
      return `${startMonth} ~ ${endMonth}`
    }

    return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`
  }

  return null;
}

const getOtdLtByBucket = (bucket, otdLtDays) => {
  if (isNull(otdLtDays) || otdLtDays === 0) {
    return 0;
  }

  switch (bucket) {
    case DAY:
      return Math.round(otdLtDays); // 3.7 -> 4일 뒤
    case WEEK:
      return Math.floor(otdLtDays / 7); // 3.7 -> 0주 뒤, 7.1 -> 1주 뒤
    case MONTH:
      return Math.floor(otdLtDays / 30);  // 3.7 -> 0달 뒤, 33 -> 1달 뒤
    default:
      return 0;
  }
}

const getRpOrderQtyConsideredMoqAndIoq = (value, moqValue, ioqValue) => {
  if (value > 0) {
    const moq = isNull(moqValue) ? 0 : moqValue;
    const ioq = isNull(ioqValue) || ioqValue === 0 ? 1 : ioqValue;

    if (moq <= 0) {
      const remainder = value % ioq;
      return remainder > 0 ? value - remainder + ioq : value;
    } else {
      if (moq > value) {
        return moq;
      }

      const valueExceptMoq = value - moq;
      const remainder = valueExceptMoq % ioq;
      return remainder > 0 ? value - remainder + ioq : value;
    }
  }

  return 0;
}

const getShipmentQty = (bohQty, arrivalQty, demandQty) => {
  const minValue = Math.min(bohQty + arrivalQty, demandQty);
  return minValue < 0 ? 0 : minValue;
}

const getShipmentQtyUpToOtdLtDays = (simSalesData, simulationHeaders, currentIdx, addedOtdLtDaysBucketIdx, isShift) => {
  let expectedShipmentQty = 0;

  if (isShift){
    addedOtdLtDaysBucketIdx += 1;
  }

  for (let idx = currentIdx + 1; idx <= addedOtdLtDaysBucketIdx; idx++) {
    if (idx < simulationHeaders.length) {
      const shipmentQty = simSalesData[simulationHeaders[idx]];
      expectedShipmentQty += shipmentQty;
    }
  }

  return expectedShipmentQty;
}

const getOpenRpOrder = (simRpOrderData, simulationHeaders, arrivalCfmData, currentIdx, bucketIdxByOtdLtDays) => { 
  let openRpOrder = 0;
  const startIdx = Math.max(currentIdx - bucketIdxByOtdLtDays + 1, 0);
  for (let idx = startIdx; idx < currentIdx; idx++) {
    const rpOrderQty = simRpOrderData[simulationHeaders[idx]];
    openRpOrder += rpOrderQty;
  }

  const planBucketSize = simulationHeaders.length;

  for (let idx = currentIdx + 1; idx <= currentIdx + bucketIdxByOtdLtDays; idx++) {
    if (idx >= planBucketSize) {
      break;
    }

    const arrivalCfm = arrivalCfmData[simulationHeaders[idx]];
    openRpOrder += arrivalCfm;
  }

  return openRpOrder;
}

const getForecastDemandQty = (demandData, addedOtdLtDaysBucketIdx, bucketIdxByOtdLtDays) => {
  let forecastDemandQty = 0;
  for (let idx = addedOtdLtDaysBucketIdx; idx <= addedOtdLtDaysBucketIdx + bucketIdxByOtdLtDays && idx < demandData.length; idx++) {
    const demandQty = demandData[idx];
    forecastDemandQty += demandQty;
  }

  return forecastDemandQty;
}

const getNetRequiredQtyByOrderMethod = (baseData, eohQty, targetStock, forecastDemandQty, expectedShipmentQty, openRpOrder, moq) => {
  const { policy, reorder } = baseData;
  const targetStockQty = targetStock ? targetStock : 0;
  let reorderQty = reorder ? reorder : 0;

  let adjustedTargetInvQty = targetStockQty + expectedShipmentQty;
  if ((isEqual(policy, P_SYSTEM) || isEqual(policy, MIN_MAX_SYSTEM)) && forecastDemandQty > 0) {
    adjustedTargetInvQty = Math.max(adjustedTargetInvQty, forecastDemandQty + targetStockQty);
  }

  if (isEqual(policy, P_SYSTEM)) {
    reorderQty = adjustedTargetInvQty;
  }

  if ((eohQty + openRpOrder - expectedShipmentQty > reorderQty) && reorderQty !== 0) {
    return 0;
  }

  switch (policy) {
    case P_SYSTEM:
    case MIN_MAX_SYSTEM:
      return adjustedTargetInvQty - (eohQty + openRpOrder);
    case Q_SYSTEM:
      return moq;
    case BASE_STOCK_SYSTEM:
      return targetStockQty - (eohQty + openRpOrder);
    default:
      return 0;
  }
}

const getBohWithExpInvQtyRemoved = (simSalesData, expInvData, simulationHeaders, eohQty, currentIdx) => {
  const expInvQty = expInvData[currentIdx];
  const prevExpIdx = expInvData.findLastIndex((invData, invIdx) => invIdx < currentIdx && invData > 0);

  let accumulateShipmentQty = 0;
  for (let idx = prevExpIdx + 1; idx <= currentIdx; idx++) {
    accumulateShipmentQty += simSalesData[simulationHeaders[idx]];
  }

  return accumulateShipmentQty < expInvQty ? eohQty - (expInvQty - accumulateShipmentQty) : eohQty;
}

const resetShipmentQtyUpToOtdLtDays = (simBohData, simArrivalData, demandData, simSalesData, simBackorderData, simulationHeaders, currentIdx, addedOtdLtDaysBucketIdx, isShift, isBackorder) => {
  for (let idx = currentIdx; idx <= addedOtdLtDaysBucketIdx + (isShift ? 1 : 0); idx++) {
    if (idx < simulationHeaders.length) {
      const headerText = simulationHeaders[idx];

      const bohQty = simBohData[headerText];
      const arrivalQty = simArrivalData[headerText];
      const demandQty = demandData[idx];
      const prevBackOrderQty = idx > 0 ? simBackorderData[simulationHeaders[idx-1]] : 0;

      const availArrivalQty = isShift ? 0 : arrivalQty;
      const salesQty = getShipmentQty(bohQty, availArrivalQty, demandQty + prevBackOrderQty);
      simSalesData[headerText] = salesQty;

      const backorderQty = isBackorder ? Math.max(0, demandQty + prevBackOrderQty - salesQty) : 0;
      simBackorderData[headerText] = backorderQty;

      if (idx + 1 < simulationHeaders.length) {
        const eohQty = Number(bohQty + arrivalQty - salesQty);
        simBohData[simulationHeaders[idx + 1]] = eohQty;
      }
    }
  }
}

const getBucketDate = (bucketUnit, date) => {
  const targetDate = new Date(date);

  if (!date) {
    return null;
  }
  
  let bucketDate;
  switch (bucketUnit) {
    case WEEK:
      bucketDate = startOfWeek(targetDate, { weekStartsOn: 1 });
      break;
      
    case MONTH:
      bucketDate = startOfMonth(targetDate);
      break;
      
    case DAY:
    default:
      bucketDate = targetDate;
  }
  
  return dateUtils.formatDate(bucketDate, 'yyyy-MM-dd');
}

const RpUtil = {
  configDefaultValue,
  getToolTip,
  getOtdLtByBucket,
  getRpOrderQtyConsideredMoqAndIoq,
  getShipmentQty,
  getShipmentQtyUpToOtdLtDays,
  getOpenRpOrder,
  getForecastDemandQty,
  getNetRequiredQtyByOrderMethod,
  getBohWithExpInvQtyRemoved,
  resetShipmentQtyUpToOtdLtDays,
  getBucketDate
};

export default RpUtil;